/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from './a11y.js';

/**
 * Inline lightbox component. Appends a fullscreen overlay inside `container`
 * and fills it with arbitrary HTML via {@link Lightbox#show}.
 *
 * @constructor
 * @param {HTMLElement} container - The element that hosts the lightbox overlay.
 * @param {object} [options]
 * @param {HTMLElement} [options.header] - A header element whose height is
 *   subtracted from the available lightbox height.
 * @param {function(HTMLElement): void} [options.onClose] - Called with the
 *   content element just before the lightbox closes.
 * @param {function(HTMLElement): void} [options.onShow] - Called with the
 *   content element after new HTML has been injected.
 * @param {string} [options.ariaLabel] - Accessible name for the dialog.
 */
export const Lightbox = function (container, options) {
    options = options || {};

    let isOpen = false;
    let bg = null;
    let content = null;
    let releaseTrap = null;

    let header = options.header;
    if (!header) {
        header = null;
        Array.from(container.children).forEach((child) => {
            if (child.nodeName.toLowerCase() === 'header') header = child;
        });
    }

    function updateLayout() {
        if (!isOpen) {
            return;
        }
        const width = `${window.innerWidth}px`;
        const height = `${
            window.innerHeight - (header !== null ? header.clientHeight : 0)
        }px`;
        bg.style.width = width;
        bg.style.height = height;
        content.style.maxHeight = height;
    }
    updateLayout();
    window.addEventListener('resize', updateLayout);

    function stopPropagation(event) {
        event.stopPropagation();
    }

    /** Opens the lightbox overlay. No-op if already open. @returns {void} */
    this.open = () => {
        if (isOpen) {
            return;
        }

        bg = document.createElement('div');
        content = document.createElement('div');
        bg.classList.add('lightbox__bg');
        content.classList.add('lightbox__container');
        // A full-screen overlay over the map: modal, so neither Tab nor a
        // screen reader walks into the points rendered behind it.
        content.setAttribute('role', 'dialog');
        content.setAttribute('aria-modal', 'true');
        content.setAttribute('tabindex', '-1');
        if (options.ariaLabel) {
            content.setAttribute('aria-label', options.ariaLabel);
        }

        bg.appendChild(content);
        container.appendChild(bg);
        container.classList.add('lightbox');

        isOpen = true;

        releaseTrap = a11y?.trapFocus(content, {
            modal: true,
            initialFocus: content,
            onEscape: () => this.close()
        });

        ['mousedown', 'mouseup', 'touchstart', 'wheel', 'dblclick'].forEach(
            (eventName) => bg.addEventListener(eventName, stopPropagation)
        );

        content.addEventListener('click', (event) => {
            if (event.target === content) {
                event.preventDefault();
                this.close();
            }
        });
    };

    /** Closes the lightbox overlay and removes it from the DOM. No-op if already closed. @returns {void} */
    this.close = () => {
        if (!isOpen) {
            return;
        }
        if (options.onClose !== undefined) {
            options.onClose.call(this, content);
        }
        isOpen = false;

        releaseTrap?.();
        releaseTrap = null;

        bg.removeChild(content);
        ['mousedown', 'mouseup', 'touchstart', 'wheel', 'dblclick'].forEach(
            (eventName) => bg.removeEventListener(eventName, stopPropagation)
        );
        container.removeChild(bg);
        container.classList.remove('lightbox');
        content = null;
        bg = null;
    };

    /**
     * Injects HTML into the lightbox content area, opening it first if needed.
     * Scripts inside the HTML are executed via `createContextualFragment`.
     * If a `.lightbox__close > a` element is present in the injected markup,
     * it is wired to close the lightbox.
     * @param {string} html - HTML string to render.
     * @returns {void}
     */
    this.show = (html) => {
        if (!isOpen) {
            this.open();
        }

        content.innerHTML = '';
        // Use createContextualFragment to allow execution of JavaScript
        const range = document.createRange();
        range.setStart(content, 0);
        content.appendChild(range.createContextualFragment(html));

        // Use close button if it exists
        const close = content.querySelector('.lightbox__close');
        if (close !== null) {
            const closeButton = close.querySelector('button, a');
            if (closeButton) {
                closeButton.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.close();
                });
            }
        }

        updateLayout();

        if (options.onShow !== undefined) {
            options.onShow.call(this, content);
        }
    };
};

export default Lightbox;
